import { useMemo } from 'react';
import { useSelector, type DefaultRootState } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import {
  appPersistSlice,
  buildAppPrefix,
  useGetDemoSuffix,
  type PersistSliceSuffix,
} from '@bbkAdminRedux/rtkq/app-persist/app-persist.slice';
import { BBKRequestContext } from '@bbkAdminRedux/rtkq/bbkApi.slice';
import {
  DEFAULT_BRAND_ID,
  useGetSelectedBrand,
} from '@bbkAdminRedux/rtkq/brands.slice';
import { playSlice } from '@bbkAdminRedux/rtkq/play.slice';
import { type ReportFilter } from '@bbkAdminRedux/rtkq/reports.slice';
import { type OfferPresentation } from '@bbkAdminUtils/api-client/rest-offers-api';
import { type EnumLike } from '@bbkAdminUtils/utility-types';

export enum ReportsFilterType {
  experiences = 'experiences',
  audiences = 'audiences',
  plays = 'plays',
  languages = 'languages',
  predictive_dashboard = 'predictive_dashboard',
  delivery = 'delivery',
}

export type ExperienceFilter = {
  field: 'funnel';
  operator: 'equals';
  value: number;
};

export type AudienceFilter = {
  field: 'funnel_audiences';
  operator: 'equals';
  value: `${number},${string}` | `${number},${string},${string}`;
};

export type GlobalFunnelAudience = {
  field: 'funnel_audiences';
  operator: 'equals';
  value: `${number},${'control' | 'fallback'}`;
};

export type PlayFilter =
  | {
      field: 'play';
      operator: 'equals';
      value: string;
    }
  | GlobalFunnelAudience;

/** Report filter for session content language (matches reporting `language` column). */
export type LanguageFilter = {
  field: 'language';
  operator: 'equals';
  value: string;
};

export type DeliveryFilter = EnumLike<typeof OfferPresentation>;
export type ReportsFilterState = {
  type: ReportsFilterType;
  brandFilter?: string;
  experiencesFilters?: ExperienceFilter[];
  audiencesFilters?: AudienceFilter[];
  playsFilters?: PlayFilter[];
  languagesFilters?: LanguageFilter[];
  deliveryFilters?: DeliveryFilter[];
  lastBrandFilter?: string;
};

export const defaultReportFilterState: ReportsFilterState = {
  type: ReportsFilterType.experiences,
};

export const isLanguagesFilterAvailable = (
  isMultiLanguage: boolean,
  enabledLocaleCount: number
): boolean => isMultiLanguage && enabledLocaleCount > 1;

export const shouldRenderLanguagesFilter = (
  isMultiLanguage: boolean,
  enabledLocaleCount: number,
  isLanguagesLoading: boolean,
  isLanguagesFetching: boolean
): boolean =>
  isMultiLanguage &&
  (isLanguagesLoading || isLanguagesFetching || enabledLocaleCount > 1);

export const resolveReportsFilterType = (
  filterType: ReportsFilterType,
  isMultiLanguage: boolean,
  enabledLocaleCount: number
): ReportsFilterType => {
  if (
    filterType === ReportsFilterType.languages &&
    !isLanguagesFilterAvailable(isMultiLanguage, enabledLocaleCount)
  ) {
    return defaultReportFilterState.type;
  }
  return filterType;
};

export const resolveReportsFilterState = (
  filter: ReportsFilterState,
  isMultiLanguage: boolean,
  enabledLocaleCount: number
): ReportsFilterState => {
  const type = resolveReportsFilterType(
    filter.type,
    isMultiLanguage,
    enabledLocaleCount
  );
  if (type === filter.type) return filter;
  return {
    ...filter,
    type,
    languagesFilters: undefined,
  };
};

export const selectFiltersFromReportsFilterState = (
  filter: ReportsFilterState
): (ExperienceFilter | AudienceFilter | PlayFilter | LanguageFilter)[] => {
  if (filter.type === ReportsFilterType.audiences) {
    return filter.audiencesFilters ?? [];
  }
  if (filter.type === ReportsFilterType.experiences) {
    return filter.experiencesFilters ?? [];
  }
  if (filter.type === ReportsFilterType.plays) {
    return filter.playsFilters ?? [];
  }
  if (filter.type === ReportsFilterType.languages) {
    return filter.languagesFilters ?? [];
  }
  return [];
};

export const selectReportFilterForPicker =
  (suffix: PersistSliceSuffix = '') =>
  (state: DefaultRootState) => {
    const appKey = selectAppId(state);
    const prefix = buildAppPrefix(appKey, suffix);
    return (
      state[appPersistSlice.name][prefix]?.reportsFilter ??
      defaultReportFilterState
    );
  };

export const selectExperiencesAudiencesPlaysReportFilter = (
  suffix: PersistSliceSuffix = ''
) =>
  createSelector(
    (state: DefaultRootState) => selectReportFilterForPicker(suffix)(state),
    selectFiltersFromReportsFilterState
  );

export const selectDeliveryReportFilter = (suffix: PersistSliceSuffix = '') =>
  createSelector(
    (state: DefaultRootState) => selectReportFilterForPicker(suffix)(state),
    (filter) => filter.deliveryFilters ?? []
  );

export const useResolvePlaysFromDeliveryFilter = (): ReportFilter[] => {
  const appKey = useSelector(selectAppId);
  const brandId = useGetSelectedBrand(appKey) ?? DEFAULT_BRAND_ID;

  const suffix = useGetDemoSuffix();
  const filters = useSelector(selectDeliveryReportFilter(suffix));
  const noNeedToFetch = filters.length === 0 || filters.length === 4;
  const { data: playsList = [] } = playSlice.endpoints.listPlays.useQuery(
    {
      appKey,
      brandId,
      context: BBKRequestContext.lifecycle_reports,
    },
    { skip: noNeedToFetch }
  );

  return useMemo(() => {
    if (noNeedToFetch) return [];
    return playsList
      .filter((x) => filters.includes(x.presentation))
      .map((x) => ({
        field: 'play_id',
        operator: 'equals',
        value: x.id,
      }));
  }, [filters, noNeedToFetch, playsList]);
};
